import { CrossroadsEntityType, CrossroadsMappingType } from '@api/types'
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { objectStore } from '../utils/objectStore'

interface CrossroadsObject {
  value: string
  label: string
  description: string
  mapping_type: CrossroadsMappingType
  entity_type: CrossroadsEntityType
}

interface ObjectContextType {
  selectedObject: CrossroadsObject | null
  setSelectedObject: (object: string | null) => void
  availableObjects: CrossroadsObject[]
}

const ObjectContext = createContext<ObjectContextType | undefined>(undefined)

const CROSSROADS_OBJECTS: CrossroadsObject[] = [
  {
    value: 'terminal',
    label: 'Terminals',
    description: 'Fuel Terminals',
    mapping_type: 'terminal',
    entity_type: 'Terminal',
  },
  {
    value: 'product',
    label: 'Products',
    description: 'Petroleum products',
    mapping_type: 'product',
    entity_type: 'Product',
  },
  {
    value: 'site',
    label: 'Sites',
    description: 'C-stores',
    mapping_type: 'site',
    entity_type: 'Site',
  },
  {
    value: 'counterparty',
    label: 'Companies',
    description: 'Counterparties',
    mapping_type: 'counterparty',
    entity_type: 'Company',
  },
]

const STORAGE_KEY = 'crossroads-selected-object'

export function ObjectProvider({ children }: { children: ReactNode }) {
  // Initialize from localStorage or default to null (All)
  const [selectedObjectValue, setSelectedObjectValue] = useState<string | null>(
    () => {
      const stored = localStorage.getItem(STORAGE_KEY)
      // Ensure the stored value is valid, otherwise use null for "All"
      if (stored && CROSSROADS_OBJECTS.some((obj) => obj.value === stored)) {
        return stored
      }
      return CROSSROADS_OBJECTS[0].value
    },
  )

  // Wrapper to persist to localStorage
  /**
   * Provide the value of the object to be selected here. This will refresh selectedObject with the object whose value matches.
   * Pass null to select "All" (no specific object selected).
   * @param value Value of the object to set, or null for "All"
   */
  const setSelectedObject = (value: string | null) => {
    setSelectedObjectValue(value)
    if (value === null) {
      localStorage.removeItem(STORAGE_KEY)
    } else {
      localStorage.setItem(STORAGE_KEY, value)
    }
  }

  // Sync with global object store
  // TODO: Remove, I think this is irrelevant
  // useEffect(() => {
  //   objectStore.setObject(selectedObject)
  // }, [selectedObject])

  const selectedObject = useMemo(() => {
    if (selectedObjectValue === null) {
      return null
    }
    const obj = CROSSROADS_OBJECTS.filter(
      (co) => co.value == selectedObjectValue,
    )
    if (obj.length == 1) {
      return obj[0]
    } else {
      console.error(
        `There was no object in CROSSROADS_OBJECTS with value ${selectedObjectValue}`,
      )
      return null
    }
  }, [selectedObjectValue])

  return (
    <ObjectContext.Provider
      value={{
        selectedObject,
        setSelectedObject,
        availableObjects: CROSSROADS_OBJECTS,
      }}
    >
      {children}
    </ObjectContext.Provider>
  )
}

export function useObject() {
  const context = useContext(ObjectContext)
  if (context === undefined) {
    throw new Error('useObject must be used within an ObjectProvider')
  }
  return context
}
